import { useStorage } from '@vueuse/core'
import { type Ref } from 'vue'
import { isUuid } from '~/utils/errors'

type Client = {
  client_id_pk: string;
  client_name: string;
  client_address: string;
  client_mode: string;
  client_initialized: boolean;
  client_billing_cycle: 'month' | 'year' | 'instant' | 'none';
  client_data_endpoint: string;
  client_license: {
    employeeCount: number;
    freeSeats: number;
    subscriptionStatus: string;
  };
  domain: {
    adConnected: boolean,
    entraConnected: boolean
  }
}

// 'not-found': the client does not exist or is not visible for the current user
// 'failed': loading broke for another reason (already reported via toast)
export type InitializeClientResult = 'loaded' | 'not-found' | 'failed'

export const useCurrentClient = (): {
  initializeCurrentClient: (uuid: string) => Promise<InitializeClientResult>;
  currentClient: Ref<Client, Client>;
  setCurrentClientUUID: (uuid: string) => void;
  getCurrentClientIdToSchema: () => string;
  getCurrentClientUUID: () => string
} => {
  const { $gql, $api, $logger, $toast } = useNuxtApp()
  const { getFreshSession, handleDeadSession } = useAuthToken()
  const defaultResponse: Client = {
    client_id_pk: '',
    client_name: '',
    client_address: '',
    client_mode: '',
    client_initialized: false,
    client_billing_cycle: null,
    client_license: {
      employeeCount: 0,
      freeSeats: 2,
      subscriptionStatus: 'active'
    },
    domain: {
      adConnected: false,
      entraConnected: false
    },
    client_data_endpoint: ''
  }

  const currentClient = useState('currentClient', () => {
    return defaultResponse
  })

  const clients = useStorage('clients', () => {
    return [defaultResponse]
  }, sessionStorage, // invalidates on tab close
  { listenToStorageChanges: true })

  const setCurrentClient = (c: Client): void => {
    if (!c) {
      throw new Error('Cannot set current client to undefined or null')
    }

    // check if already existing in array, if so, update, otherwise push
    const index = clients.value.findIndex(cc => cc.client_id_pk === c.client_id_pk)
    if (index > -1) {
      clients.value[index] = c
    } else {
      clients.value.push(c)
    }

    currentClient.value = c
  }

  // check if client properties are good, otherwise fetch
  function isClientGood (client: Client): boolean {
    if (!client) {
      return false
    }

    if (!Object.hasOwn(client, 'client_license')) {
      return false
    }
    if (client.client_license.subscriptionStatus !== 'active') {
      return false
    }
    if (!client.client_initialized) {
      return false
    }
    if (!Object.hasOwn(client, 'client_data_endpoint')) {
      return false
    }

    return true
  }

  const initializeCurrentClient = async (uuid: string): Promise<InitializeClientResult> => {
    try {
      if (!uuid) {
        throw new Error('Cannot initialize current client with empty uuid')
      }

      // a malformed id can never match a client; don't even ask Hasura (it would reject the uuid)
      if (!isUuid(uuid)) {
        $logger.warn(`Client id ${uuid} is not a valid uuid`)
        return 'not-found'
      }

      // check if client with this uuid exists and license data is exiting, otherwwise fetch
      const index = clients.value.findIndex(cc => cc.client_id_pk === uuid)
      if (index > -1 && isClientGood(clients.value[index])) {
        setCurrentClient(clients.value[index])
        return 'loaded'
      }

      const currentClientQuery = `
        query client($clientUUID: uuid!) {
          client: tbl_client(where: { client_id_pk: { _eq: $clientUUID } }) {
            client_name
            client_id_pk
            client_mode
            client_address
            client_initialized
            client_billing_cycle
            client_data_endpoint
          }
        }`

      const currentClientDetailsQuery = `{
          ad: tbl_status_collector(where: { client_uuid: { _eq: "${uuid}" }}) {
              version
          }
        }
    `

      const { data: dataMain } = await $gql.query(currentClientQuery, {
        clientUUID: uuid
      }, 'main')

      if (dataMain.client.length === 0) {
        $logger.warn(`Client ${uuid} not found`)
        return 'not-found'
      }

      let client
      if (dataMain.client[0].client_initialized) {
        client = dataMain.client[0]

        // set current client information so that data endpoint is available for next queries;
        // domain isn't known yet at this point, so default it to avoid exposing a client without
        // a domain field to pages reading currentClient.domain while the details query below is in flight
        client.domain = { adConnected: false, entraConnected: false }
        setCurrentClient(client)

        // get license information
        client.client_license = await $api.client.getClientLicense(uuid)

        // a disconnected Entra keeps its domain row, so only the api's entra meta (tenant id still
        // set) tells whether Entra is connected -- an Entra status error must not fail the client load
        const [{ data: dataDetails }, entraConnected] = await Promise.all([
          $gql.query(currentClientDetailsQuery),
          $api.client.getEntraMeta(uuid)
            .then(entraMeta => entraMeta.status === 'active')
            .catch((e) => {
              $logger.error(`Could not fetch entra connection status ${e}`)
              return false
            })
        ])

        // get ad status
        const adConnected = (dataDetails?.ad?.length ?? 0) > 0 && dataDetails.ad[0].version !== null

        client.domain = { adConnected, entraConnected }
      } else {
        client = dataMain.client[0]
        client.domain = { adConnected: false, entraConnected: false }
      }

      setCurrentClient(client)
      return 'loaded'
    } catch (e) {
      $logger.error(e)

      // Loading a client fails for plenty of reasons that have nothing to do with the
      // session (unknown uuid, GraphQL hiccup, an API that rejects our token because it
      // validates against a different Keycloak instance). Signing out on those turns a
      // data problem into an endless login loop and hides the real cause, so end the
      // session only when it is genuinely gone - getFreshSession resolves to null exactly
      // then. If even that check fails, assume the session is fine: staying signed in with
      // a broken page beats an unwarranted sign-out, and a truly dead session still
      // converges via the 401 handling in the api/graphql plugins.
      const sessionIsAlive = await getFreshSession({ force: true })
        .then(session => Boolean(session))
        .catch(() => true)

      if (sessionIsAlive) {
        $logger.warn('Failed to initialize current client, keeping the session')
        $toast.error('Kunde konnte nicht geladen werden', 'Bitte die Seite neu laden. Details stehen im Log.')
        return 'failed'
      }

      $logger.warn('Session lost while initializing current client, logging out user')
      await handleDeadSession()
      return 'failed'
    }
  }

  const setCurrentClientUUID = (uuid: string): void => {
    uuid = uuid.replace(/_/g, '-')

    // check if client with this uuid exists, otherwise create new empty
    const index = clients.value.findIndex(cc => cc.client_id_pk === uuid)
    if (index === -1) {
      const initClient = defaultResponse
      initClient.client_id_pk = uuid
      setCurrentClient(initClient)
    }
  }

  const getCurrentClientIdToSchema = (): string => {
    return currentClient.value.client_id_pk.replace(/-/g, '_')
  }

  const getCurrentClientUUID = (): string => {
    if (currentClient.value.client_id_pk) {
      return currentClient.value.client_id_pk
    } else {
      return ''
    }
  }

  return {
    initializeCurrentClient,
    currentClient,
    setCurrentClientUUID,
    getCurrentClientIdToSchema,
    getCurrentClientUUID
  }
}
